import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy, ExternalLink, Mail, Eye, Check, Inbox, Bell, Send, BellRing, BellOff,
  Globe as GlobeIcon, DollarSign, RotateCcw, Info,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import Money from "@/components/Money";
import { isPushSupported, subscribeToPush, unsubscribeFromPush, currentSubscription, sendTestPush } from "@/lib/push";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ManageSubscription from "@/components/ManageSubscription";

export default function Settings() {
  const [tab, setTab] = useState(() => {
    // Deep-link support: /app/settings#billing opens the Manage plan tab.
    if (typeof window !== "undefined" && window.location.hash === "#billing") {
      return "billing";
    }
    return "public";
  });
  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Module 15" title="Settings" subtitle="Public profile, currency, notifications, digest and push preferences." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap" data-testid="settings-tabs">
          <TabsTrigger value="public" data-testid="tab-public">Public Profile</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Manage Plan</TabsTrigger>
          <TabsTrigger value="currency" data-testid="tab-currency">Currency & Region</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">Email Preferences</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox">Recruiter Inbox</TabsTrigger>
          <TabsTrigger value="digest" data-testid="tab-digest">Digest & Emails</TabsTrigger>
          <TabsTrigger value="push" data-testid="tab-push">Push</TabsTrigger>
        </TabsList>
        <TabsContent value="public"><PublicSettings /></TabsContent>
        <TabsContent value="billing"><ManageSubscription /></TabsContent>
        <TabsContent value="currency"><CurrencySettings /></TabsContent>
        <TabsContent value="emails"><EmailPreferences /></TabsContent>
        <TabsContent value="inbox"><RecruiterInbox /></TabsContent>
        <TabsContent value="digest"><DigestSettings /></TabsContent>
        <TabsContent value="push"><PushSettings /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Email Preferences (per-category toggles) ----
function EmailPreferences() {
  const [cats, setCats] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/notifications/categories"),
      api.get("/notifications/preferences"),
    ]).then(([c, p]) => {
      setCats(c.data.categories || []);
      setPrefs(p.data.preferences || {});
    }).catch(() => toast.error("Load failed"));
  }, []);

  const toggle = async (key, current) => {
    setBusy(true);
    try {
      await api.put("/notifications/preferences", { category: key, enabled: !current });
      setPrefs((p) => ({ ...p, [key]: !current }));
      toast.success(!current ? "Subscribed" : "Unsubscribed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const unsubAll = async () => {
    if (!window.confirm("Unsubscribe from ALL non-transactional Career OS emails? Security & billing emails will still be sent.")) return;
    setBusy(true);
    try {
      await api.post("/notifications/preferences/unsubscribe-all");
      const p = await api.get("/notifications/preferences");
      setPrefs(p.data.preferences || {});
      toast.success("Unsubscribed from all marketing emails");
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="email-preferences">
      <section className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-mono uppercase tracking-widest text-primary">Email preferences</div>
            <div className="text-xs text-muted-foreground mt-1">Toggle categories you want to receive.</div>
          </div>
          <button onClick={unsubAll} disabled={busy} className="text-xs text-destructive underline hover:no-underline disabled:opacity-50" data-testid="unsub-all-btn">Unsubscribe from all</button>
        </div>
        <div className="divide-y divide-border">
          {cats.map((c) => {
            const enabled = c.locked ? true : (prefs[c.key] !== undefined ? prefs[c.key] : c.default);
            return (
              <div key={c.key} className="flex items-start justify-between py-3 gap-4" data-testid={`pref-row-${c.key}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {c.label}
                    {c.locked && <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[9px] font-mono uppercase">always on</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{c.desc}</div>
                </div>
                <label className={`relative inline-flex items-center cursor-pointer ${c.locked ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <input
                    type="checkbox"
                    disabled={c.locked || busy}
                    checked={enabled}
                    onChange={() => !c.locked && toggle(c.key, enabled)}
                    className="sr-only peer"
                    data-testid={`pref-toggle-${c.key}`}
                  />
                  <div className="w-10 h-5 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/40 rounded-full peer-checked:bg-primary after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"></div>
                </label>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ---- Currency & Region ----
function CurrencySettings() {
  const { currency, source, supported, ratesTs, ratesStale, setPreference, resetAuto, refresh, format } = useCurrency();
  const [profile, setProfile] = useState(null);
  useEffect(() => { api.get("/profile").then(r => setProfile(r.data)); }, []);

  const sample = 1250.55;
  const sourceLabels = {
    profile: "Your profile country",
    preference: "Your manual override",
    ip: "Auto-detected from your IP",
    locale: "Browser locale",
    fallback: "Default (USD)",
    "auto": "Auto-detect (on next reload)",
  };

  return (
    <div className="space-y-6" data-testid="currency-settings">
      <section className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary mb-3">
          <GlobeIcon size={12} /> Currency & Region
        </div>
        <div className="text-sm text-muted-foreground mb-4">
          All monetary values across CareerOS — salaries, plans, financial projections, portfolio comp —
          are displayed in your preferred currency. Underlying data is stored in USD; conversion happens
          on display.
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="border border-border rounded-lg p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Active currency</div>
            <div className="font-display font-black text-2xl">{currency}</div>
            <div className="text-xs text-muted-foreground mt-1">{sourceLabels[source] || source || "—"}</div>
          </div>
          <div className="border border-border rounded-lg p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Sample formatting</div>
            <div className="font-mono font-semibold text-2xl">{format(sample)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Rates fetched {ratesTs ? new Date(ratesTs).toLocaleString() : "—"}
              {ratesStale && <span className="text-[hsl(var(--warning))]"> · cached (provider unavailable)</span>}
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="ccy-select">Preferred currency</Label>
          <select
            id="ccy-select"
            data-testid="currency-select"
            value={currency}
            onChange={(e) => setPreference(e.target.value).then(() => toast.success(`Currency set to ${e.target.value}`))}
            className="mt-1.5 w-full h-11 rounded-md border border-input bg-background px-3 text-sm font-mono"
          >
            {supported.map((c) => (
              <option key={c.code} value={c.code}>{`${c.code} — ${c.name}`}</option>
            ))}
          </select>
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="outline" size="sm" onClick={() => resetAuto().then(() => window.location.reload())}
              data-testid="currency-reset" className="gap-2"
            >
              <RotateCcw size={13} /> Restore auto-detection
            </Button>
            <div className="text-xs text-muted-foreground flex items-start gap-1 max-w-md leading-relaxed">
              <Info size={11} className="shrink-0 mt-0.5" />
              Changing currency updates all monetary values across CareerOS instantly. Your preference is saved to your account and syncs across devices.
            </div>
          </div>
        </div>
      </section>

      <section className="bg-card border border-border rounded-xl p-6" data-testid="country-region-section">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Country & Region</div>
        <div className="text-sm text-muted-foreground mb-4">
          Country lets AI use local labor-market data (Toronto, London, Sydney, etc.) rather than converting a US salary.
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Country code (ISO 3166-1 alpha-2)</Label>
            <Input
              data-testid="country-code-input"
              defaultValue={profile?.country_code || ""}
              onBlur={async (e) => {
                const v = (e.target.value || "").toUpperCase().slice(0, 2);
                await api.put("/profile", { country_code: v });
                await refresh();
                toast.success(`Country set to ${v || "auto"}`);
              }}
              placeholder="CA, US, GB, AU, JP, IN…"
              className="mt-1.5 font-mono uppercase"
              maxLength={2}
            />
          </div>
          <div>
            <Label>Country name (free-text)</Label>
            <Input
              data-testid="country-input"
              defaultValue={profile?.country || ""}
              onBlur={async (e) => { await api.put("/profile", { country: e.target.value }); }}
              placeholder="Canada, United Kingdom…"
              className="mt-1.5"
            />
          </div>
        </div>
      </section>

      <section className="bg-card border border-border rounded-xl p-6" data-testid="currency-examples">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Sample values in your currency</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Coffee", v: 5 },
            { label: "Book", v: 25.99 },
            { label: "Course", v: 499 },
            { label: "Junior salary", v: 55000 },
            { label: "Senior salary", v: 145000 },
            { label: "Total comp", v: 285000 },
            { label: "Home", v: 750000 },
            { label: "Lifetime earnings", v: 3500000 },
          ].map((r) => (
            <div key={r.label} className="border border-border rounded-md p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{r.label}</div>
              <div className="font-mono font-semibold text-base mt-0.5"><Money value={r.v} from="USD" /></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ---- Public Profile (unchanged from before) ----
function PublicSettings() {
  const [profile, setProfile] = useState(null);
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null);
  const [reason, setReason] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ public_enabled: false, public_username: "", public_bio: "", recruit_message: "", show_salary: false, show_email: false });

  useEffect(() => {
    api.get("/profile").then(r => {
      setProfile(r.data);
      setForm({
        public_enabled: !!r.data.public_enabled,
        public_username: r.data.public_username || "",
        public_bio: r.data.public_bio || "",
        recruit_message: r.data.recruit_message || "",
        show_salary: !!r.data.show_salary,
        show_email: !!r.data.show_email,
      });
    });
  }, []);

  const checkUsername = async (uname) => {
    if (!uname || uname.length < 3) { setAvailable(null); setReason(null); return; }
    setChecking(true);
    try { const { data } = await api.get(`/public/username-check/${uname}`); setAvailable(data.available); setReason(data.reason || null); }
    catch { setAvailable(null); setReason(null); } finally { setChecking(false); }
  };

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/profile/public", form); setProfile(data); toast.success("Public profile updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const publicUrl = form.public_username && form.public_enabled ? `${window.location.origin}/u/${form.public_username}` : null;

  return (
    <div className="space-y-6" data-testid="public-settings">
      <div className="bg-card border border-border rounded-xl p-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="public-enable" type="checkbox" checked={form.public_enabled} onChange={e => setForm({ ...form, public_enabled: e.target.checked })} className="w-4 h-4 accent-primary" />
          <div>
            <div className="font-display font-bold text-lg">Enable public profile</div>
            <div className="text-xs text-muted-foreground">Make yourself discoverable to recruiters via a shareable public URL.</div>
          </div>
        </label>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <Label>Custom username</Label>
        <div className="flex items-stretch gap-2 mt-1.5">
          <div className="flex items-center bg-secondary border border-r-0 border-border rounded-l-md px-3 text-sm text-muted-foreground">{window.location.origin}/u/</div>
          <Input data-testid="public-username" value={form.public_username} onChange={e => { const v = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''); setForm({ ...form, public_username: v }); checkUsername(v); }} className="rounded-l-none flex-1" placeholder="janedoe" />
        </div>
        <div className="mt-2 text-xs font-mono">
          {checking && <span className="text-muted-foreground">Checking...</span>}
          {!checking && available === true && <span className="text-[hsl(var(--success))] flex items-center gap-1"><Check size={12} /> Available</span>}
          {!checking && available === false && <span className="text-destructive">{reason === "Reserved" ? "Reserved — pick another" : reason === "Invalid format" ? "Invalid format" : "Username taken"}</span>}
        </div>
        {publicUrl && (
          <div className="mt-3 flex items-center gap-2 border border-border bg-background p-3 rounded-md" data-testid="public-url">
            <div className="text-xs font-mono text-muted-foreground truncate flex-1">{publicUrl}</div>
            <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><Copy size={12} /></button>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs text-primary flex items-center gap-1"><ExternalLink size={12} /></a>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <Label>Bio (shown on public profile)</Label>
        <Textarea data-testid="public-bio-input" value={form.public_bio} onChange={e => setForm({ ...form, public_bio: e.target.value })} rows={4} className="mt-1.5" placeholder="Product Manager who ships. 8 years scaling B2B SaaS from 0→$50M ARR..." />
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <Label>"Recruit me" pitch (headline)</Label>
        <Input data-testid="recruit-message-input" value={form.recruit_message} onChange={e => setForm({ ...form, recruit_message: e.target.value })} className="mt-1.5" placeholder="Open to Staff / Principal PM roles in fintech or AI" />
      </div>

      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Visibility controls</div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="show-salary" type="checkbox" checked={form.show_salary} onChange={e => setForm({ ...form, show_salary: e.target.checked })} className="w-4 h-4 accent-primary" />
          <div className="flex-1"><div className="text-sm font-medium">Show salary range publicly</div><div className="text-xs text-muted-foreground">Signals transparency but may filter offers.</div></div>
        </label>
      </div>

      <Button data-testid="public-save" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save public profile"}</Button>
    </div>
  );
}

// ---- Recruiter Inbox ----
function RecruiterInbox() {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { load(); }, []);
  const load = () => api.get("/recruiter-messages").then(r => setMsgs(r.data));
  const markRead = async (id) => {
    try { await api.post(`/recruiter-messages/${id}/read`); load(); }
    catch (e) { /* eslint-disable-next-line no-console */ console.debug("[Settings] markRead:", e); }
  };
  return (
    <div data-testid="recruiter-inbox">
      {msgs.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-10 text-center text-muted-foreground">
          <Inbox size={40} className="mx-auto mb-4 text-muted-foreground/60" />
          Enable your public profile to start receiving recruiter messages.
        </div>
      ) : (
        <div className="space-y-3">
          {msgs.map(m => (
            <div key={m.id} className={`bg-card border border-border rounded-xl p-5 ${m.read ? "opacity-60" : ""}`} data-testid={`inbox-msg-${m.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-display font-bold text-lg">{m.from_name} <span className="text-muted-foreground font-normal text-sm">from {m.from_company || "—"}</span></div>
                  <a href={`mailto:${m.from_email}`} className="text-xs text-primary hover:underline flex items-center gap-1"><Mail size={11} /> {m.from_email}</a>
                </div>
                {!m.read && <button data-testid={`inbox-read-${m.id}`} onClick={() => markRead(m.id)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><Eye size={12} /> Mark read</button>}
              </div>
              {(m.role_offered || m.salary_range) && (
                <div className="flex gap-3 text-xs font-mono mb-3">
                  {m.role_offered && <div className="bg-primary/10 border border-primary/40 text-primary px-2 py-0.5 rounded">{m.role_offered}</div>}
                  {m.salary_range && <div className="bg-[hsl(var(--success))/0.1] border border-[hsl(var(--success))/0.4] text-[hsl(var(--success))] px-2 py-0.5 rounded">{m.salary_range}</div>}
                </div>
              )}
              <div className="text-sm text-foreground/80 whitespace-pre-wrap">{m.message}</div>
              <div className="mt-3 text-[10px] font-mono uppercase text-muted-foreground">{new Date(m.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Digest & Push (kept same) ----
function DigestSettings() {
  const { user, setUser } = useAuth();
  const [prefs, setPrefs] = useState({ email_enabled: true, digest_enabled: true, digest_frequency: "weekly", notify_on_recruiter_message: true, notify_on_cert_expiring: true, notify_on_job_status: true });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (user) setPrefs({
      email_enabled: user.email_enabled ?? true,
      digest_enabled: user.digest_enabled ?? true,
      digest_frequency: user.digest_frequency || "weekly",
      notify_on_recruiter_message: user.notify_on_recruiter_message ?? true,
      notify_on_cert_expiring: user.notify_on_cert_expiring ?? true,
      notify_on_job_status: user.notify_on_job_status ?? true,
    });
  }, [user]);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/notification-prefs", prefs); setUser(data); toast.success("Preferences saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const sendNow = async () => {
    setSending(true);
    try { await api.post("/digest/send"); toast.success(`Digest sent to ${user?.email}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); } finally { setSending(false); }
  };
  const previewIt = async () => {
    try { const { data } = await api.get("/digest/preview"); setPreview(data.html); } catch { toast.error("Preview failed"); }
  };
  return (
    <div className="space-y-6" data-testid="digest-settings">
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary mb-3"><Bell size={12} /> Master switch</div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="pref-email" type="checkbox" checked={prefs.email_enabled} onChange={e => setPrefs({ ...prefs, email_enabled: e.target.checked })} className="w-4 h-4 accent-primary" />
          <div><div className="text-sm font-bold">Allow email notifications</div><div className="text-xs text-muted-foreground">Turn off to stop all email from Career OS.</div></div>
        </label>
      </div>
      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Weekly digest</div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input data-testid="pref-digest" type="checkbox" checked={prefs.digest_enabled} onChange={e => setPrefs({ ...prefs, digest_enabled: e.target.checked })} className="w-4 h-4 accent-primary" />
            <div className="flex-1"><div className="text-sm font-bold">Send weekly digest emails</div><div className="text-xs text-muted-foreground">Every Monday: your XP, job matches, salary gap, cert expiries, recruiter messages.</div></div>
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={previewIt} data-testid="digest-preview-btn">Preview digest</Button>
          <Button size="sm" onClick={sendNow} disabled={sending} data-testid="digest-send-btn" className="gap-2"><Send size={12} />{sending ? "Sending..." : "Send me one now"}</Button>
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Transactional email triggers</div>
        <Toggle label="Recruiter contact received" checked={prefs.notify_on_recruiter_message} onChange={v => setPrefs({ ...prefs, notify_on_recruiter_message: v })} tid="pref-recruiter" />
        <Toggle label="Certification expiring soon" checked={prefs.notify_on_cert_expiring} onChange={v => setPrefs({ ...prefs, notify_on_cert_expiring: v })} tid="pref-cert" />
        <Toggle label="Job status change (offer / rejection)" checked={prefs.notify_on_job_status} onChange={v => setPrefs({ ...prefs, notify_on_job_status: v })} tid="pref-job" />
      </div>
      <Button data-testid="prefs-save" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save preferences"}</Button>
      {preview && (
        <div className="mt-6" data-testid="digest-preview">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-bold">Preview</div>
            <button onClick={() => setPreview(null)} className="text-xs text-muted-foreground hover:text-foreground">Close</button>
          </div>
          <iframe title="digest" srcDoc={preview} className="w-full h-[700px] border border-border bg-white rounded-md" />
        </div>
      )}
    </div>
  );
}

function PushSettings() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => { const ok = await isPushSupported(); setSupported(ok); if (ok) { const sub = await currentSubscription(); setSubscribed(!!sub); } })(); }, []);
  const enable = async () => { setBusy(true); try { await subscribeToPush(); setSubscribed(true); toast.success("Push notifications enabled"); } catch (e) { toast.error(e.message || "Failed"); } finally { setBusy(false); } };
  const disable = async () => { setBusy(true); try { await unsubscribeFromPush(); setSubscribed(false); toast.success("Unsubscribed"); } catch { toast.error("Failed"); } finally { setBusy(false); } };
  const test = async () => { setBusy(true); try { const r = await sendTestPush(); toast.success(`Test push sent (${r.delivered}/${r.total} delivered)`); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  return (
    <div className="space-y-6" data-testid="push-settings">
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary mb-3"><BellRing size={12} /> Browser push notifications</div>
        <div className="text-sm text-muted-foreground mb-4">Get real-time desktop alerts for cert expirations, recruiter contacts, and interview reminders — even when Career OS isn't open.</div>
        {!supported ? (
          <div className="text-sm text-[hsl(var(--warning))]" data-testid="push-unsupported">This browser doesn't support push notifications. Try Chrome, Edge, or Firefox on desktop.</div>
        ) : subscribed ? (
          <div className="flex gap-2 items-center" data-testid="push-status-on">
            <div className="text-sm text-[hsl(var(--success))] flex items-center gap-2"><Check size={14} /> Push notifications enabled</div>
            <Button variant="outline" size="sm" onClick={test} disabled={busy} data-testid="push-test" className="ml-2">Send test</Button>
            <Button variant="outline" size="sm" onClick={disable} disabled={busy} data-testid="push-disable" className="gap-1"><BellOff size={12} /> Disable</Button>
          </div>
        ) : (
          <Button onClick={enable} disabled={busy} data-testid="push-enable" className="gap-2"><BellRing size={14} /> Enable push notifications</Button>
        )}
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, tid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input data-testid={tid} type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="w-4 h-4 accent-primary" />
      <div className="text-sm text-foreground/80">{label}</div>
    </label>
  );
}
